<template>
  <div>
    <template v-if="readOnly">
      <div :class="{ 'right-align': singleLine }" class="verdi">
        <template v-if="modelValue !== null && modelValue !== undefined && userPreferences">
          <FormatNumber :decimal-sep="userPreferences.numberFormatDecimalSeparator"
                        :precision="props.header.numberFormatDecimalPrecision"
                        :thousand-sep="getThousandsSeparator(userPreferences)"
                        :use-precision="props.header.basicDataType === 'numeric' || props.header.basicDataType === 'decimal'"
                        :value="modelValue" />
        </template>
        <template v-else class="verdi muted">
          (blank)
        </template>
      </div>
    </template>
    <template v-else>
      <div class="input-number-container">
        <div class="input-wrapper">
          <BaseInput :clearable="header.isNullable" :error="hasError" :error-message="errorMessage" :model-value="formattedModelValue"
                     type="text" @change="changed" />
        </div>
      </div>
      <BaseProgressBar v-if="saving" />
      <div v-else style="height: 4px;padding: 0;margin: 0;" />
    </template>
  </div>
</template>

<script setup>
import { onMounted, ref } from 'vue';
import { storeToRefs } from 'pinia';
import { useTableStore } from '@/store/storeTable';
import FormatNumber from '@/components/singleComponents/FormatNumber.vue';
import { useUserPreferencesStore } from '@/store/storeUserPreferences.js';
import { format } from '@/helpers/NumberFormatLib.js';
import BaseInput from '@/components/BaseComponents/BaseInput.vue';
import BaseProgressBar from '@/components/BaseComponents/BaseProgressBar.vue';

const userPreferencesStore = useUserPreferencesStore();
const { userPreferences } = storeToRefs(useUserPreferencesStore());

const props = defineProps({
  modelValue: {
    type: [String, Number],
    default: null,
  },
  readOnly: {
    type: Boolean,
    default: false,
  },
  singleLine: {
    type: Boolean,
    default: false,
  },
  autoSave: {
    type: Boolean,
    default: true,
  },
  rowId: { type: [String, Number], default: '' },
  header: { type: Object, required: true },
});

const emit = defineEmits(['update:modelValue']);
const hasError = ref(false);
const errorMessage = ref('');
const saving = ref(false);

const tableStore = useTableStore();
const formattedModelValue = ref(props.modelValue);

// use "" as thousands separator if column setup states that the separator should not be shown
const getThousandsSeparator = () => {
  return props.header.numberFormatShowThousandsSeparator
    ?
    userPreferences.value.numberFormatThousandsSeparator
    :
    '';
};

const formatModelValue = async () => {
  await userPreferencesStore.getUserPreferences();
  formattedModelValue.value = formatInputValue(props.modelValue);
};

const formatInputValue = (newValue) => {
  // Hvis verdi er tom streng eller null, returner tom streng
  if (newValue === '' || newValue === null || newValue === undefined) {
    return '';
  }

  return format(newValue, {
    thousandSep: getThousandsSeparator(),
    decimalSep: userPreferences.value.numberFormatDecimalSeparator,
    usePrecision: props.header.basicDataType === 'numeric',
    precision: props.header.numberFormatDecimalPrecision,
  });
};

onMounted(async () => {
  await formatModelValue();
});

async function changed (newValue) {
  if (newValue === '' || newValue === null) {
    if (props.header.isNullable) {
      emit('update:modelValue', null);
      if (!props.autoSave) return;
      const item = { rowId: props.rowId, databaseObjectId: props.header.databaseObjectId, columnName: props.header.field, oldValue: props.modelValue, newValue: null };
      hasError.value = false;
      saving.value = true;
      await tableStore.saveData(item);
      saving.value = false;
      return;
    } else {
      hasError.value = true;
      errorMessage.value = 'Feltet kan ikke være tomt';
      return;
    }
  }
  let parsedValue = newValue.trim().replaceAll(' ', '');
  parsedValue = parsedValue.replaceAll(userPreferences.value.numberFormatThousandsSeparator, '');
  parsedValue = parsedValue.replaceAll(userPreferences.value.numberFormatDecimalSeparator, '.');

  // strip everything after last dot
  if (props.header.basicDataType === 'int' && parsedValue.indexOf('.') !== -1) {
    parsedValue = parsedValue.slice(0, parsedValue.lastIndexOf('.'));
  }

  if (!isNumeric(parsedValue)) {
    hasError.value = true;
    errorMessage.value = `Ugyldig tallformat. Bruk kun tall sammen med tusenskilletegn '${userPreferences.value.numberFormatThousandsSeparator}'
     og desimaltegn '${userPreferences.value.numberFormatDecimalSeparator}'.`;
    console.error('Invalid numeric value', newValue, parsedValue);
    formattedModelValue.value = newValue;
    return;
  }

  emit('update:modelValue', parsedValue);

  if (!props.autoSave) return;

  const item = {
    rowId: props.rowId,
    databaseObjectId: props.header.databaseObjectId,
    columnName: props.header.field,
    oldValue: props.modelValue,
    newValue: parsedValue,
  };

  hasError.value = false;
  saving.value = true;
  await tableStore.saveData(item);

  formattedModelValue.value = formatInputValue(parsedValue);

  saving.value = false;
}

// https://stackoverflow.com/a/175787
function isNumeric (str) {
  if (typeof str != 'string') return false; // we only process strings!
  return !isNaN(str) && // use type coercion to parse the _entirety_ of the string (`parseFloat` alone does not do this)...
    !isNaN(parseFloat(str)); // ...and ensure strings of whitespace fail
}

</script>
<style scoped>
.right-align {
  text-align: right;
  width: 100%;
}

.input-number-container {
  padding: 8px;
  width: 100%;
}

.verdi {
  padding: 8px;
}

.verdi.single-line {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  width: inherit;
  max-width: inherit;
  font-style: normal;
}

.verdi.muted {
  color: var(--gray-500);
  font-style: italic;
}

.input-wrapper {
  position: relative;
  display: flex;
  align-items: center;
  width: 100%;
}

.clear-button {
  position: absolute;
  right: 8px;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  color: var(--gray-500);
  font-size: 1.2rem;
  cursor: pointer;
  padding: 4px 8px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.clear-button:hover {
  background-color: var(--gray-200);
  color: var(--gray-700);
}
</style>
